import { FC, Ref, useEffect, useMemo, useState } from "react";
import { Control, FieldValues, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  MultiOptionsDataType,
} from "../../../../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../../../../hooks/useSiteCore";

import { useGetProjectDeliverablesByProjectId } from "../../../../../../../../../../../api/services/project/projectApis";
import LabelWithInfoIcon from "../../../../../../../../../../../components/labelWithInfoIcon/LabelWithInfoIcon";
import { ManageLocale } from "../../../../../../../../../../../components/manageLocale/ManageLocale";
import { MAX_LENGTH_75 } from "../../../../../../../../../../../helpers/constants";
import { KeyValue } from "../../../../../../../../../../../helpers/types/common";
import {
  setApiDataToDropDownOption,
  setApiDataToDropDownOptionForActivityType,
} from "../../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../../../../hooks/useNavigationSiteCore";
import { userPreferredLanguageSelector } from "../../../../../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../../../../../../../../redux/slices/project/projectSelector";
import { MdmSearchActivityResultRowValue } from "../../../../../../../../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  ProjectCreateActivityGeneralFormKeys,
  ProjectCreateActivityGeneralProps,
} from "../../../modelsAndSchemas/models";
import { addActivitySiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

interface ProjectCreateActivityProps extends FieldValues {
  control: Control<ProjectCreateActivityGeneralProps>;
  pageId: string;
  projectDeliveryRef: Ref<{ resetValues: () => void }>;
  isPendingProjectActivity: boolean; // for loader
  children?: React.ReactNode;
  handleSaveManageLocal: (localeData: KeyValue) => void;
  manageLocaleTitle: string;
  activityTypeList: MdmSearchActivityResultRowValue[];
  manageLocaleInfoMessage?: string;
  modalityList?: MultiOptionsDataType[];
  modalityRef: Ref<{ resetValues: (values?: string[]) => void }>;
  isFrameworkActivity?: boolean;
}

export const ProjectCreateActivityGeneral: FC<ProjectCreateActivityProps> = ({
  control,
  pageId,
  projectDeliveryRef,
  isPendingProjectActivity,
  children,
  manageLocaleTitle,
  manageLocaleInfoMessage,
  handleSaveManageLocal,
  activityTypeList,
  modalityRef,
  modalityList,
  isFrameworkActivity = false,
}) => {
  const dispatch = useDispatch();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const { id: projectId = "" } = projectKeyDetails || {};
  const userSelectedLanguage = useSelector(userPreferredLanguageSelector);
  const {
    data: projectActivityDeliverables,
    isLoading: projectActivityDeliverablesPending,
    isError: isProjectActivityDeliverablesError,
    isSuccess: isProjectActivityDeliverablesSuccess,
    refetchWithParams: refetchProjectActivityDeliverables,
  } = useGetProjectDeliverablesByProjectId();

  const [showManageLocalePopup, setShowManageLocalePopup] = useState(false);
  const handleShowManageLocale = () => {
    setShowManageLocalePopup(true);
  };

  const onClickCloseShowManageLocale = () => {
    setShowManageLocalePopup(false);
  };

  const topicNames = useWatch({
    name: ProjectCreateActivityGeneralFormKeys.topic,
    control,
  });

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          projectActivityDeliverablesPending || isPendingProjectActivity,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [projectActivityDeliverablesPending, isPendingProjectActivity]);

  useEffect(() => {
    if (isProjectActivityDeliverablesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProjectActivityDeliverablesError]);

  useEffect(() => {
    refetchProjectActivityDeliverables(projectId);
  }, []);

  const projectActivityTypesDropdownOption = useMemo(() => {
    if (!activityTypeList.length) {
      return [];
    }
    return setApiDataToDropDownOptionForActivityType(activityTypeList);
  }, [activityTypeList]);

  return (
    <>
      <MuiBox data-testid="activity-tab-general">
        <MuiGrid container className="activity-tab-general">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateActivityGeneralFormKeys.activityType}
            >
              <MuiSelect
                label={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.activityType,
                )}
                options={projectActivityTypesDropdownOption}
                visibleDefaultSelectOption
                showSearch
                id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.activityType}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                disabled={isFrameworkActivity}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateActivityGeneralFormKeys.modality}
              ref={modalityRef}
            >
              <MuiMultiSelect
                label={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.modality,
                )}
                showSearch
                showChip
                showSelectAllOption
                options={modalityList ?? []}
                id={`${pageId}-${addActivitySiteCoreKeys.modality}`}
                chipId={`${pageId}-${addActivitySiteCoreKeys.modality}-chip`}
                value=""
                dialogTitle={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.modality,
                )}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
                isMandatory
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiBox className="topic-name">
              <FormController
                control={control}
                name={`${ProjectCreateActivityGeneralFormKeys.topic}.${userSelectedLanguage}`}
              >
                <MuiTextField
                  label={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.topic,
                  )}
                  id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.topic}`}
                  isMandatory
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  showCloseIcon
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
              <MuiLink
                className="topic-manage-locale-link"
                id={`${pageId}-topic-locale`}
                data-testid={`${pageId}-topic-locale`}
                onClick={handleShowManageLocale}
              >
                {commonLabels.locale}
              </MuiLink>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateActivityGeneralFormKeys.deliverables}
              ref={projectDeliveryRef}
            >
              <MuiMultiSelect
                label={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.deliverables,
                )}
                options={setApiDataToDropDownOption(
                  projectActivityDeliverables,
                )}
                showSearch
                showChip
                showSelectAllOption
                id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.deliverables}`}
                chipId={`${pageId}-${ProjectCreateActivityGeneralFormKeys.deliverables}-chip`}
                dialogTitle={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.deliverables,
                )}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
            {!projectActivityDeliverables?.length &&
              isProjectActivityDeliverablesSuccess && (
                <LabelWithInfoIcon
                  label={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.noDeliverablesAvailable,
                  )}
                  tooltipMessage={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.noTaggedDeliverablesTooltip,
                  )}
                />
              )}
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateActivityGeneralFormKeys.activityStatus}
            >
              <MuiSelect
                label={fetchLabel(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.activityStatus,
                )}
                options={fetchDropdownValues(
                  addActivitySiteCoreKeys.addActivity,
                  addActivitySiteCoreKeys.statusList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.activityStatus}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
        <MuiBox>
          <MuiTypography
            variant="h2Bold"
            className="select-applicable"
            component="h2"
          >
            {fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.defaultActivityValues,
            )}
          </MuiTypography>
        </MuiBox>
        {children}
        <MuiBox>
          <MuiTypography
            id="id-group-label"
            variant="h2Bold"
            className="select-applicable"
            component="h2"
          >
            {fetchLabel(
              addActivitySiteCoreKeys.addActivity,
              addActivitySiteCoreKeys.selectPreferencesHeading,
            )}
          </MuiTypography>
        </MuiBox>
        <MuiBox role="group" aria-labelledby="id-group-label">
          <MuiGrid role="list" container className="checkbox-container">
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ProjectCreateActivityGeneralFormKeys.triggerBilling}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.triggerBilling,
                  )}
                  id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.triggerBilling}`}
                  data-testid={`${pageId}-${ProjectCreateActivityGeneralFormKeys.triggerBilling}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ProjectCreateActivityGeneralFormKeys.lastContactDate}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.countsTowardsLastContactDate,
                  )}
                  id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.lastContactDate}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ProjectCreateActivityGeneralFormKeys.triggerTouch}
              >
                <MuiCheckbox
                  checkboxLabel={fetchLabel(
                    addActivitySiteCoreKeys.addActivity,
                    addActivitySiteCoreKeys.triggerTouchPointSurvey,
                  )}
                  id={`${pageId}-${ProjectCreateActivityGeneralFormKeys.triggerTouch}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
      {showManageLocalePopup && (
        <ManageLocale
          show={showManageLocalePopup}
          maxLength={MAX_LENGTH_75}
          handleCloseModal={onClickCloseShowManageLocale}
          localeLanguages={topicNames}
          onSaveManageLocalCallback={handleSaveManageLocal}
          title={manageLocaleTitle}
          bannerInfoMessage={manageLocaleInfoMessage}
        />
      )}
    </>
  );
};
